/**
 * LoadingSkeleton - Displays loading state while data is being fetched
 *
 * Shows a visually appropriate skeleton that matches the dark theme
 * and provides feedback that the app is loading.
 */

interface LoadingSkeletonProps {
  /** Type of content being loaded */
  variant?: 'map' | 'journal' | 'full';
  /** Optional message to display */
  message?: string;
}

export function LoadingSkeleton({
  variant = 'full',
  message = 'Loading your memories...',
}: LoadingSkeletonProps) {
  if (variant === 'map') {
    return (
      <div
        className="h-full w-full bg-bg-base flex items-center justify-center"
        role="status"
        aria-live="polite"
        aria-label="Loading map"
      >
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full bg-bg-surface animate-pulse"
            aria-hidden="true"
          />
          <p className="text-text-secondary text-sm">{message}</p>
        </div>
      </div>
    );
  }

  if (variant === 'journal') {
    return (
      <div
        className="h-full w-full bg-bg-base p-4"
        role="status"
        aria-live="polite"
        aria-label="Loading journal"
      >
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Skeleton cards */}
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-bg-surface rounded-xl p-4 animate-pulse"
              aria-hidden="true"
            >
              <div className="flex gap-4">
                <div className="w-20 h-20 bg-bg-elevated rounded-lg shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-bg-elevated rounded w-3/4" />
                  <div className="h-3 bg-bg-elevated rounded w-1/2" />
                  <div className="h-3 bg-bg-elevated rounded w-2/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-text-secondary text-sm text-center mt-4">{message}</p>
      </div>
    );
  }

  // Full page loading state
  return (
    <div
      className="min-h-screen bg-bg-base flex items-center justify-center p-4"
      role="status"
      aria-live="polite"
      aria-label="Loading application"
    >
      <div className="text-center">
        <div
          className="w-20 h-20 mx-auto mb-6 rounded-full bg-bg-surface animate-pulse flex items-center justify-center"
          aria-hidden="true"
        >
          <span className="text-3xl">🗺️</span>
        </div>
        <p className="text-text-secondary">{message}</p>
      </div>
    </div>
  );
}
